/**
 * PricingPage — public /pricing page. Indicative launch pricing with
 * "Register interest" waitlist capture (no payments yet).
 */
import { useState } from "react";
import axios from "axios";

import { API } from "../lib/beaconUtils";

const TIERS = [
  {
    id: "basic",
    name: "Basic",
    price: "A$99",
    per: "/month",
    yearly: "or A$990/year — 2 months free",
    blurb: "One site, fully covered.",
    features: [
      "1 site",
      "Luma — your AI safety comms assistant",
      "AI-tailored annual comms plan",
      "3-page toolbox talk packs (PPTX)",
      "A4 posters, email & intranet comms",
      "Company branding on every output",
      "Australian legislation & vernacular built in",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "A$249",
    per: "/month",
    yearly: "or A$2,490/year — 2 months free",
    blurb: "Every site, its own plan.",
    highlight: true,
    features: [
      "Everything in Basic, including Luma",
      "Multi-site annual plans (up to 20 sites)",
      "Per-site calendars, cadence & topic control",
      "Full-year toolbox pack — one branded download",
      "Real site examples woven into comms",
      "Quick Comms for urgent incidents",
      "Priority support",
    ],
  },
];

export default function PricingPage({ onSignInGoogle }) {
  const [tier, setTier] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(), name: name.trim() || null, tier,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="beacon-hero beacon-pricing" data-testid="pricing-page">
      <div className="beacon-hero-grain" aria-hidden="true" />
      <header className="beacon-hero-topbar beacon-hero-topbar-sticky">
        <a className="beacon-brand beacon-pricing-brand" href="/"
          data-testid="pricing-home-link">
          <span className="beacon-wordmark">BEACON</span>
          <span className="beacon-divider">//</span>
          <span className="beacon-tagline">H&amp;S COMMS, CALIBRATED.</span>
        </a>
        <div className="beacon-hero-topbar-actions">
          <a className="beacon-hero-topbar-link" href="/"
            data-testid="pricing-back-link">← Back</a>
          <button type="button" className="beacon-hero-topbar-cta"
            data-testid="pricing-signin"
            onClick={onSignInGoogle}>Sign in</button>
        </div>
      </header>

      <section className="beacon-pricing-body">
        <div className="beacon-tiny-label">INDICATIVE LAUNCH PRICING</div>
        <h1 className="beacon-pricing-title">
          A year of safety comms,<br />priced like one lunch shout.
        </h1>
        <p className="beacon-pricing-sub">
          Beacon is in a closed pilot. Register your interest below and
          we&rsquo;ll be in touch when your spot opens up.
        </p>

        <div className="beacon-pricing-grid">
          {TIERS.map((t) => (
            <div key={t.id}
              className={`beacon-pricing-card${t.highlight ? " is-highlight" : ""}`}
              data-testid={`pricing-tier-${t.id}`}>
              {t.highlight && (
                <span className="beacon-pricing-flag">MOST POPULAR</span>
              )}
              <div className="beacon-pricing-card-name">{t.name}</div>
              <div className="beacon-pricing-card-price">
                {t.price}<span>{t.per}</span>
              </div>
              <div className="beacon-pricing-card-yearly">{t.yearly}</div>
              <div className="beacon-pricing-card-blurb">{t.blurb}</div>
              <ul className="beacon-pricing-card-features">
                {t.features.map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
              <button type="button" className="beacon-cta beacon-cta-compact"
                data-testid={`pricing-register-${t.id}`}
                onClick={() => {
                  setTier(t.id);
                  setDone(false);
                  document.getElementById("waitlist-form")?.scrollIntoView({
                    behavior: "smooth", block: "center",
                  });
                }}>
                Register interest →
              </button>
            </div>
          ))}
        </div>

        <div id="waitlist-form" className="beacon-pricing-waitlist"
          data-testid="waitlist-form">
          {done ? (
            <div className="beacon-pricing-success" data-testid="waitlist-success">
              <strong>You&rsquo;re on the list.</strong> We&rsquo;ll email you
              when your {tier === "pro" ? "Pro" : "Basic"} spot opens up.
            </div>
          ) : (
            <>
              <div className="beacon-tiny-label">
                REGISTER INTEREST{tier ? ` · ${tier.toUpperCase()}` : ""}
              </div>
              <div className="beacon-pricing-waitlist-fields">
                <input className="beacon-input" type="text"
                  data-testid="waitlist-name"
                  placeholder="Your name (optional)"
                  value={name} maxLength={120}
                  onChange={(e) => setName(e.target.value)} />
                <input className="beacon-input" type="email"
                  data-testid="waitlist-email"
                  placeholder="Work email"
                  value={email} maxLength={254}
                  onChange={(e) => setEmail(e.target.value)} />
                <button type="button" className="beacon-cta beacon-cta-compact"
                  data-testid="waitlist-submit"
                  disabled={busy || !email.includes("@")}
                  onClick={register}>
                  {busy ? "Registering…" : "Join the waitlist →"}
                </button>
              </div>
              {error && (
                <div className="beacon-tab-error" data-testid="waitlist-error">
                  {error}
                </div>
              )}
              <p className="beacon-pricing-footnote">
                No payment now. No card. Just a heads-up when Beacon opens.
              </p>
            </>
          )}
        </div>
      </section>

      <footer className="beacon-hero-footer">
        <span>Beacon</span>
        <span>Australian-built, Australian-vernacular</span>
      </footer>
    </div>
  );
}
